import { Engine } from 'json-rules-engine'
import { TopLevelCondition } from 'json-rules-engine'
import { trackPendoEvent } from '@/util/helper'

export enum NotificationPriority {
  Critical = 1,
  // High = 5,
  Warning = 10,
  General = 15,
}

export const NotificationPriorityMapping = {
  [NotificationPriority.Critical]: 'critical',
  [NotificationPriority.Warning]: 'warning',
  [NotificationPriority.General]: 'general',
}

// create a color mapper for the notification severity
export const notificationBannerSeverityColorMapper = {
  [NotificationPriority.Critical]: '#FAD1CE',
  // [NotificationPriority.High]: '#F9E7B8',
  [NotificationPriority.Warning]: '#F9E7B8',
  [NotificationPriority.General]: '#B2CCFF',
}

// create a color mapper for the notification drawer severity
export const notificationDrawerSeverityColorMapper = {
  [NotificationPriority.Critical]: '#FAD1CE',
  // [NotificationPriority.High]: '#F9E7B8',
  [NotificationPriority.Warning]: '#F9E7B8',
  [NotificationPriority.General]: '#D1E0FF',
}

export enum VisibilityLevel {
  Account = 'account',
  Agency = 'agency',
}

export enum NotificationStatusType {
  All = 'all',
  Viewed = 'viewed',
  NotViewed = 'not_viewed',
}

export enum NotificationFrequencyUnit {
  Once = 'once',
  Always = 'always',
  Minutes = 'minutes',
  Hours = 'hours',
  Days = 'days',
  Weeks = 'weeks',
  Months = 'months',
  Years = 'years',
}

export interface INotificationFrequency {
  unit: NotificationFrequencyUnit
  interval?: number
}

interface IReportingNotificationButton {
  text: string
  conditions?: TopLevelCondition
  redirect?: {
    name: string
    params?: { [key: string]: string }
    query?: { [key: string]: string }
  }
  url?: string
  action?: {
    type: NotificationActionType
    data?: Record<string, unknown>
  }
}

export interface IReportingNotificationButtonShow extends IReportingNotificationButton {
  show: boolean
}

interface IReportingNotificationVisibility {
  type: VisibilityLevel
  conditions: TopLevelCondition
}

export interface IReportingNotification {
  id: string
  title: string
  priority: NotificationPriority
  description?: string
  primaryButton?: IReportingNotificationButton
  visibility: IReportingNotificationVisibility[]
  conditions?: TopLevelCondition
  externalAPIConfig?: {
    serviceName: string
    route: string
    method: string
    query: { [key: string]: string }
  }
  frequency?: INotificationFrequency
  nextScheduledAt: number
}

export interface IReportingNotificationViewed extends IReportingNotification {
  viewed: boolean
}

export enum NotificationTabType {
  System = 'system',
  Alert = 'alert',
}

export async function getNotificationPrimaryButton(
  notificationId: string,
  button: IReportingNotificationButtonShow,
  options: { location; user; company; route }
) {
  if (!button) return null
  button.show = !!(button.text && (button.url || button.redirect))

  if (button.conditions) {
    const ruleEngine = new Engine([], {
      allowUndefinedFacts: true,
      allowUndefinedConditions: true,
    })
    ruleEngine.addFact('location', options.location)
    ruleEngine.addFact('company', options.company)
    ruleEngine.addFact('user', options.user)
    ruleEngine.addFact('route', options.route)

    ruleEngine.addRule({
      conditions: button.conditions,
      event: {
        type: notificationId,
        params: button,
      },
    })
    const results = await ruleEngine.run()
    button.show = !!results.events.length
  }

  return button
}

export function getDirectoryPayload({ location, user, company }) {
  return {
    location: JSON.parse(JSON.stringify(location)),
    user: {
      id: user?.id,
      companyId: user?.company_id,
      email: user?.email,
      phone: user?.phone,
      role: user?.role,
      type: user?.type,
      firstName: user?.first_name,
      lastName: user?.last_name,
      name: `${user?.first_name} ${user?.last_name}`,
      fullName: `${user?.first_name} ${user?.last_name}`,
    },
    company: {
      id: company?.id,
    },
  }
}

const getValueFromPath = (obj, path) => {
  return path.split('.').reduce((acc, part) => acc && acc[part], obj)
}

export const getValuesForButton = (redirect, options) => {
  const directoryPayload = getDirectoryPayload(options)
  for (const key in redirect.params) {
    redirect.params[key] = getValueFromPath(directoryPayload, redirect.params[key]) || redirect.params[key]
  }
  for (const key in redirect.query) {
    redirect.query[key] = getValueFromPath(directoryPayload, redirect.query[key]) || redirect.query[key]
  }

  return redirect
}

export const noAlertMessages = [
  { emoji: '😎', message: 'common.notification.allSetNoNewAlerts' },
  { emoji: '🚀', message: 'common.notification.youreAllCaughtUpNoAlertsPending' },
  { emoji: '✨', message: 'common.notification.shinyAndCleanNoNewAlerts' },
  { emoji: '✅', message: 'common.notification.noNewAlertsYoureGoodToGo' },
  { emoji: '🌞', message: 'common.notification.clearSkiesNoAlertsToShow' },
  { emoji: '📭', message: 'common.notification.yourInboxIsQuietNoNewAlerts' },
  { emoji: '🥳', message: 'common.notification.woohooNoNewAlertsKeepUpGoodWork' },
  { emoji: '👍', message: 'common.notification.noAlertsWaitingEverythingInOrder' },
  { emoji: '🎉', message: 'common.notification.niceNoNewAlerts' },
  { emoji: '🔔', message: 'common.notification.alertSpaceClear' },
]

export enum NotificationClickedElementType {
  Navigation = 'navigation-btn',
  PrimaryButton = 'primary-btn',
  CloseButton = 'close-btn',
}

export enum NotificationType {
  Banner = 'banner',
  Drawer = 'drawer',
}

export enum NotificationActionType {
  TestNotification = 'test-notification',
  ShowReconnectModal = 'show-reconnect-modal',
  ConnectionsWithDeletedCalendars = 'connections-with-deleted-calendars',
  SocialPlannerReintegration = 'social-planner-reintegration'
}

interface ITrackNotificationClickedEvent {
  id: string
  title: string
  buttonText?: string
  priority: NotificationPriority
  currentPage: string
  clickedElement: NotificationClickedElementType
  type: NotificationType
}

interface ITrackNotificationViewedEvent {
  id: string
  title: string
  priority: NotificationPriority
  currentPage: string
  type: NotificationType
}

export const trackNotificationClickedEvent = (eventProperties: ITrackNotificationClickedEvent) => {
  trackPendoEvent('automation_notification_cta_clicked', {
    ...eventProperties,
    priority: NotificationPriorityMapping[eventProperties.priority],
  })
}

export const trackNotificationViewedEvent = (eventProperties: ITrackNotificationViewedEvent) => {
  trackPendoEvent('automation_notification_viewed', {
    ...eventProperties,
    priority: NotificationPriorityMapping[eventProperties.priority],
  })
}

export const getPendoViewedNotificationId = (notificationType: NotificationType, id: string) => {
  return `${notificationType}::${id}`
}
